'use client';

import {useState, useEffect, useRef} from 'react';

export type WindowSizes = keyof typeof WindowSizeThresholds;
export enum WindowSizeThresholds {
  /**
   * @bug First `useEffect()` in `useWindowSize()` hook fails to
   * reliably determine the window size category in certain scenarios.
   * Lowering the `MobileSmall` threshold by one pixel resolves this issue,
   * particularly for older mobile devices with a width of 375 CSS pixels.
   */
  MobileSmall = 375 - 1,
  Mobile = 450,
  MobileWide = 512,
  MobileBig = 680,
  DesktopSmall = 768,
  Desktop = 1024,
  DesktopWide = 1280
}

const initialState = Object.keys(WindowSizeThresholds)
  .filter(key => isNaN(Number(key)))
  .reduce((object, key) => {
    object[key as WindowSizes] = false;
    return object;
  }, {} as Record<WindowSizes, boolean>);

/**
 * Tracks the current window size category.
 *
 * This hook determines which size threshold, as defined in
 * {@link WindowSizeThresholds}, corresponds to the current window width.
 * It updates state dynamically as the window resizes.
 *
 * @returns An object representing the current window size state.
 * Each key corresponds to a size threshold, and its value is `true`
 * if the window width falls within that threshold, otherwise `false`.
 *
 * @todo Leverage Singleton pattern to avoid adding event listeners
 * each time this hook is used somewhere.
 */
export function useWindowSize() {
  const [size, setSize] = useState(initialState);
  const mediaQueries = useRef(
    typeof window === 'undefined'
      ? []
      : (
          Object.entries(WindowSizeThresholds) as ObjectEntries<
            typeof WindowSizeThresholds
          >
        )
          .filter(([key]) => isNaN(Number(key)))
          .map(
            ([key, value]) =>
              [key, window.matchMedia(`(max-width: ${value}px)`)] as const
          )
  );

  // The events won't trigger on the initial page load, therefore it's
  // necessary to determine the current window size category manually.
  useEffect(() => {
    for (const [key, value] of (
      Object.entries(WindowSizeThresholds) as ObjectEntries<
        typeof WindowSizeThresholds
      >
    ).filter(([key]) => isNaN(Number(key))))
      if (window.innerWidth <= value) {
        setSize({...initialState, [key]: true});
        break;
      }
  }, []);

  useEffect(() => {
    const handlers: [MediaQueryList, (event: MediaQueryListEvent) => void][] =
      [];

    for (const [windowSize, value] of mediaQueries.current) {
      const handler = (event: MediaQueryListEvent) =>
        setSize(state => ({...state, [windowSize!]: event.matches}));
      value.addEventListener('change', handler);
      handlers.push([value, handler]);
    }

    return () => {
      for (const [value, handler] of handlers)
        value.removeEventListener('change', handler);
    };
  }, []);

  return size;
}
